<!--
  AI:
  Status: "confirmed"
  Type: Page
  SubType: CrudIntegration
  Reason: Mounts an autosaving mvs-crud-object-generic for xx.XxAutosaveFocusMock to A/B the
          inPlaceUpdate flag against a live focus/caret readout, plus a second section that mounts
          the same component against a real backend entity.
-->
<!-- @ticket TM-1000002003 -->
<div class="xx-autosave-focus p-3">

    <div class="mb-3">
        <h2 class="m-0">Autosave ohne Fokusverlust</h2>
        <small class="text-color-secondary">
            Das Formular speichert sich selbst, während getippt wird. Mit <b>In-Place-Update</b> bleibt
            der Cursor im Feld stehen und ein abhängiges Feld erscheint und verschwindet, ohne die
            Eingabe zu unterbrechen. Ohne In-Place-Update wird das Formular nach jeder Antwort neu
            aufgebaut — dann geht der Fokus verloren. Der obere Abschnitt läuft gegen einen Mock im
            Arbeitsspeicher, der untere gegen einen echten Datensatz des laufenden Backends.
        </small>
    </div>

    <!-- ── Steuerung ───────────────────────────────────────────────────────────── -->
    <div class="xx-af-box p-3 mb-3 border-round-lg">
        <div class="flex flex-wrap align-items-end gap-4">

            <div class="flex align-items-center gap-2">
                <p-toggleSwitch [(ngModel)]="inPlace" (onChange)="handleInPlaceToggle()"></p-toggleSwitch>
                <span class="font-semibold">In-Place-Update</span>
                <p-tag [value]="inPlace ? 'an' : 'aus'"
                       [severity]="inPlace ? 'success' : 'danger'"></p-tag>
            </div>

            <div class="flex flex-column gap-1 w-20rem">
                <label class="text-sm text-color-secondary">Antwortzeit des Mocks</label>
                <p-select [options]="latencyOptions" optionLabel="label" optionValue="value"
                          [(ngModel)]="latencyMs" (onChange)="handleLatencyChange()"
                          styleClass="w-full"></p-select>
            </div>

            <p-button label="Formular neu aufbauen" icon="fa-regular fa-arrows-rotate"
                      severity="secondary" [outlined]="true"
                      (onClick)="handleRebuild()"></p-button>

            <small class="text-color-secondary">
                Autosave nach {{ autoSaveDebounceMs }} ms Tippruhe.
            </small>
        </div>
    </div>

    <div class="grid">

        <!-- ── links: das eingebettete Formular ────────────────────────────────── -->
        <div class="col-12 lg:col-6">
            <div class="xx-af-box p-3 border-round-lg h-full">

                <div class="flex align-items-center justify-content-between mb-2">
                    <span class="font-semibold">Formular</span>
                    <div class="flex gap-2">
                        <p-tag [value]="'Speichervorgänge: ' + saveCount" severity="info"></p-tag>
                    </div>
                </div>

                <!--
                  Ein einelementiges @for, getrackt über den Zähler: „Formular neu aufbauen“ ändert den
                  Track-Key, Angular zerstört die Komponente und baut sie neu auf.
                -->
                <div class="xx-af-mock-form">
                @for (instance of [formInstance]; track instance) {
                    <!--
                      AI: CRUD Object Generic Component — DIREKT eingebunden, NICHT über <mvs-crud-object>.
                      Der Wrapper baut seine innere Komponente bei jeder Input-Änderung neu auf; ein neues
                      Overwrite würde das Formular zerstören, bevor der In-Place-Pfad überhaupt greift.
                      Genau so bindet auch cw das Formular ein.
                    -->
                    <mvs-crud-object-generic
                        [objectType]="objectType"
                        [objectId]="objectId"
                        [autoSaveMode]="true"
                        [autoSaveDebounceMs]="autoSaveDebounceMs"
                        [inPlaceUpdate]="inPlace"
                        [formControlOverwrite]="overwrite"
                        [hideActionBar]="true"
                        [removeDelete]="true"
                        [hideAuditAction]="true"
                        [hideHistoryAction]="true"
                        (onFormChange)="handleFormChange($event)"
                        (onFormDirty)="handleFormDirty($event)"
                        (onFormStateChange)="handleFormStateChange($event)"
                        (onTemplateRefresh)="handleTemplateRefresh()"
                        (onChangedObject)="handleChangedObject($event)"
                        (onObjectChangeError)="handleObjectChangeError($event)">
                    </mvs-crud-object-generic>
                }
                </div>

                <div class="mt-3 pt-3 border-top-1 surface-border">
                    <small class="text-color-secondary">
                        <b>So reproduzieren Sie beide Defekte:</b> In-Place-Update ausschalten,
                        Antwortzeit auf 2500 ms stellen und durchgehend „abcdef“ in „Bemerkung“ tippen.
                        Nach der Antwort steht im Fokus-Feld rechts „— (Body)“, und die während des
                        Speicherns getippten Zeichen fehlen. Danach In-Place-Update einschalten und
                        dasselbe wiederholen.
                    </small>
                </div>
            </div>
        </div>

        <!-- ── rechts: Diagnose ────────────────────────────────────────────────── -->
        <div class="col-12 lg:col-6">

            <div class="xx-af-box p-3 border-round-lg mb-3">

                <div class="font-semibold mb-2">Fokus und Cursor</div>

                <div class="flex flex-wrap align-items-center gap-2 mb-2">
                    <span class="text-sm text-color-secondary">Aktives Feld:</span>
                    <p-tag [value]="focusedFieldLabel"
                           [severity]="focusedFieldLabel === '— (Body)' ? 'danger' : 'success'"></p-tag>
                </div>

                @if (caretStart !== null) {
                    <div class="text-sm">
                        Cursor: <span class="font-semibold">{{ caretStart }}</span>
                        bis <span class="font-semibold">{{ caretEnd }}</span>
                    </div>
                    <div class="xx-af-code mt-2 p-2 border-round text-sm">{{ focusedFieldValue }}</div>
                } @else {
                    <div class="text-sm text-color-secondary">
                        Keine Cursor-Position — der Fokus liegt nicht in einem Eingabefeld.
                    </div>
                }
            </div>

            <div class="xx-af-box p-3 border-round-lg mb-3">

                <div class="font-semibold mb-2">Zustand</div>

                @if (!state) {
                    <span class="text-color-secondary text-sm">
                        Noch kein Zustand veröffentlicht — das Formular lädt seine Definition.
                    </span>
                } @else {
                    <div class="flex flex-wrap gap-2">
                        <p-tag [value]="'geändert: ' + state.dirty"
                               [severity]="state.dirty ? 'warn' : 'secondary'"></p-tag>
                        <p-tag [value]="'gültig: ' + state.valid"
                               [severity]="state.valid ? 'success' : 'danger'"></p-tag>
                        <p-tag [value]="'speichert: ' + state.busy"
                               [severity]="state.busy ? 'warn' : 'secondary'"></p-tag>
                        <p-tag [value]="'„Details“ sichtbar: ' + detailsVisible"
                               [severity]="detailsVisible ? 'info' : 'secondary'"></p-tag>
                        <!-- TM-1000002003: Path C — did the server template refresh rebuild the form? -->
                        <p-tag [value]="'Template-Refresh: ' + templateRefreshMode"
                               [severity]="templateRefreshMode === 'in place' ? 'success'
                                          : templateRefreshMode === 'Neuaufbau' ? 'danger' : 'secondary'"></p-tag>
                    </div>

                    <div class="text-sm text-color-secondary mt-2">
                        onFormDirty: <span class="font-semibold">{{ dirtyOutput }}</span>
                    </div>
                }

            </div>

            <div class="xx-af-box p-3 border-round-lg mb-3">
                <div class="flex align-items-center justify-content-between mb-2">
                    <span class="font-semibold">Formularwerte</span>
                    <small class="text-color-secondary">Das steht gerade auf dem Bildschirm.</small>
                </div>

                @if (formValue) {
                    <div class="xx-af-code p-2 border-round text-sm">{{ formValue | json }}</div>
                } @else {
                    <span class="text-color-secondary text-sm">Noch keine Änderung erfasst.</span>
                }
            </div>

            <div class="xx-af-box p-3 border-round-lg">

                <div class="flex align-items-center justify-content-between mb-2">
                    <span class="font-semibold">Ereignisse</span>
                    <p-button label="Leeren" severity="secondary" [text]="true" size="small"
                              (onClick)="handleClearLog()"></p-button>
                </div>

                @if (!eventLog.length) {
                    <span class="text-color-secondary text-sm">Noch nichts passiert.</span>
                } @else {
                    <div class="xx-af-log">
                        @for (entry of eventLog; track entry.seq) {
                            <div class="flex gap-2 align-items-baseline py-1 border-bottom-1 surface-border text-sm">
                                <span class="text-color-secondary xx-af-time">{{ entry.time }}</span>
                                <p-tag [value]="entry.name" [severity]="entry.severity"></p-tag>
                                <span class="flex-1 xx-af-detail">{{ entry.detail }}</span>
                            </div>
                        }
                    </div>
                }
            </div>
        </div>
    </div>

    <!-- ── Echtes Backend-Formular ─────────────────────────────────────────────── -->
    <!--
      AI: CRUD Object Generic Component — dieselbe Komponente wie oben, nur gegen einen echten
      Datensatz des laufenden Backends. Auch hier DIREKT eingebunden und nicht über <mvs-crud-object>.
      Autosave ist bewusst zunächst AUS: ein Kunde ist kein Wegwerf-Datensatz.
    -->
    <div class="xx-af-box p-3 mt-3 border-round-lg" id="echtes-backend-formular">

        <div class="flex align-items-center justify-content-between mb-2">
            <div>
                <span class="font-semibold">Echtes Backend-Formular</span>
                <small class="block text-color-secondary">
                    Dieselbe Komponente, derselbe Schalter — nur ohne Mock. Erst „Formular laden“
                    fragt das Backend überhaupt etwas. Gespeichert wird über „Speichern“ im Formular;
                    Autosave lässt sich zusätzlich einschalten und schreibt dann wirklich.
                </small>
            </div>
            <p-tag [value]="'Speichervorgänge: ' + realSaveCount" severity="info"></p-tag>
        </div>

        <div class="flex flex-wrap align-items-end gap-4 mb-3">

            <div class="flex flex-column gap-1 w-14rem">
                <label class="text-sm text-color-secondary">Objekttyp</label>
                <input pInputText type="text" [(ngModel)]="realObjectType" class="w-full"/>
            </div>

            <div class="flex flex-column gap-1 w-10rem">
                <label class="text-sm text-color-secondary">Objekt-ID</label>
                <p-inputNumber [(ngModel)]="realObjectId" [useGrouping]="false"
                               styleClass="w-full"></p-inputNumber>
            </div>

            <div class="flex align-items-center gap-2">
                <p-toggleSwitch [(ngModel)]="realInPlace"
                                (onChange)="handleRealInPlaceToggle()"></p-toggleSwitch>
                <span class="font-semibold">In-Place-Update</span>
                <p-tag [value]="realInPlace ? 'an' : 'aus'"
                       [severity]="realInPlace ? 'success' : 'danger'"></p-tag>
            </div>

            <div class="flex align-items-center gap-2">
                <p-toggleSwitch [(ngModel)]="realAutoSave"
                                (onChange)="handleRealAutoSaveToggle()"></p-toggleSwitch>
                <span class="font-semibold">Autosave</span>
                <p-tag [value]="realAutoSave ? 'schreibt' : 'aus'"
                       [severity]="realAutoSave ? 'warn' : 'secondary'"></p-tag>
            </div>

            <p-button label="Formular laden" icon="fa-regular fa-cloud-arrow-down"
                      (onClick)="handleRealLoad()"></p-button>
        </div>

        <div class="grid">

            <div class="col-12 lg:col-7">
                @if (realNotice) {
                    <p-message severity="warn" [text]="realNotice" styleClass="w-full mb-2"></p-message>
                }

                <div class="xx-af-real-form">
                    @if (realMounted) {
                        @for (instance of [realInstance]; track instance) {
                            <mvs-crud-object-generic
                                [objectType]="realObjectType"
                                [objectId]="realObjectId"
                                [autoSaveMode]="realAutoSave"
                                [autoSaveDebounceMs]="autoSaveDebounceMs"
                                [inPlaceUpdate]="realInPlace"
                                [hideAuditAction]="true"
                                [hideHistoryAction]="true"
                                [removeDelete]="true"
                                (onFormStateChange)="handleRealFormStateChange($event)"
                                (onTemplateRefresh)="handleRealTemplateRefresh()"
                                (onChangedObject)="handleRealChangedObject($event)"
                                (onObjectChangeError)="handleRealObjectChangeError($event)">
                            </mvs-crud-object-generic>
                        }
                    } @else {
                        <span class="text-color-secondary text-sm">
                            Noch nichts geladen — Objekttyp und Objekt-ID prüfen und „Formular laden“
                            drücken.
                        </span>
                    }
                </div>
            </div>

            <div class="col-12 lg:col-5">

                <div class="font-semibold mb-2">Diagnose</div>

                <div class="flex flex-wrap align-items-center gap-2 mb-2">
                    <span class="text-sm text-color-secondary">Aktives Feld:</span>
                    <p-tag [value]="focusedFieldLabel"
                           [severity]="focusedFieldLabel === '— (Body)' ? 'danger' : 'success'"></p-tag>
                </div>

                @if (caretStart !== null) {
                    <div class="text-sm mb-2">
                        Cursor: <span class="font-semibold">{{ caretStart }}</span>
                        bis <span class="font-semibold">{{ caretEnd }}</span>
                    </div>
                } @else {
                    <div class="text-sm text-color-secondary mb-2">
                        Keine Cursor-Position — der Fokus liegt nicht in einem Eingabefeld.
                    </div>
                }

                <div class="flex flex-wrap gap-2">
                    <!-- TM-1000002003: Path C am echten Formular -->
                    <p-tag [value]="'Template-Refresh: ' + realTemplateRefreshMode"
                           [severity]="realTemplateRefreshMode === 'in place' ? 'success'
                                      : realTemplateRefreshMode === 'Neuaufbau' ? 'danger' : 'secondary'"></p-tag>
                    @if (realState) {
                        <p-tag [value]="'geändert: ' + realState.dirty"
                               [severity]="realState.dirty ? 'warn' : 'secondary'"></p-tag>
                        <p-tag [value]="'gültig: ' + realState.valid"
                               [severity]="realState.valid ? 'success' : 'danger'"></p-tag>
                        <p-tag [value]="'speichert: ' + realState.busy"
                               [severity]="realState.busy ? 'warn' : 'secondary'"></p-tag>
                    }
                </div>

                <small class="block text-color-secondary mt-3">
                    „Template-Refresh“ bleibt „—“, solange kein Feld dieses Formulars eine neue
                    Server-Definition anfordert. Alle Ereignisse stehen oben im gemeinsamen
                    Ereignisprotokoll.
                </small>
            </div>
        </div>
    </div>
</div>
